import React from 'react';
import {useTranslation} from 'react-i18next';
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Typography,
} from '@mui/material';
import dayjs from 'dayjs';

import {CalendarItem, timeLabel} from './calendarModel';

interface Props {
    item: CalendarItem | null;
    onClose: () => void;
}

/**
 * Detailansicht eines Termins oder einer Wiedervorlage.
 *
 * Die Rohfelder stehen bewusst weiterhin drin, nur nicht mehr in jeder Zeile des Kalenders: beim
 * Abgleich mit der Labelwin-Plantafel braucht man `belegart`, `headId` und die Personalnummer, im
 * Alltag stören sie.
 */
export const EntryDialog: React.FC<Props> = ({item, onClose}) => {
    const {t, i18n} = useTranslation();

    // Bewusst Intl statt dayjs' 'dddd': die dayjs-Locales sind im Projekt zwar importiert, aber
    // nie über dayjs.locale() aktiviert — Wochentage kämen sonst englisch heraus.
    const dateLabel = (value: string) =>
        new Intl.DateTimeFormat(i18n.language, {
            weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric',
        }).format(dayjs(value).toDate());

    const entry = item?.entry;
    const task = item?.task;

    return (
        <Dialog open={item !== null} onClose={onClose} maxWidth="sm" fullWidth>
            {item && (
                <>
                    <DialogTitle sx={{pb: 1}}>
                        <Stack direction="row" spacing={1}
                               sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5}}>
                            <span>{item.title}</span>
                            <Chip size="small" color={item.color} variant="outlined"
                                  label={item.kind === 'task'
                                      ? t('calendar.taskLabel')
                                      : entry?.kind ?? t('calendar.unknownKind')}/>
                        </Stack>
                    </DialogTitle>

                    <DialogContent>
                        <Stack spacing={1.5}>
                            <Typography variant="body2">
                                {dateLabel(item.date)} · {timeLabel(item, t('calendar.allDay'))}
                            </Typography>

                            {entry?.tooltip && (
                                <Typography variant="body2" color="text.secondary"
                                            sx={{whiteSpace: 'pre-wrap'}}>
                                    {entry.tooltip}
                                </Typography>
                            )}

                            {entry && (entry.orderNumber || entry.project) && (
                                <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', rowGap: 0.5}}>
                                    {entry.orderNumber && (
                                        <Chip size="small" variant="outlined"
                                              label={`${t('calendar.order')}: ${entry.orderNumber}`}/>
                                    )}
                                    {entry.orderProject && (
                                        <Chip size="small" variant="outlined"
                                              label={`${t('calendar.project')}: ${entry.orderProject}`}/>
                                    )}
                                    {entry.project && entry.project !== entry.orderProject && (
                                        <Chip size="small" variant="outlined" label={entry.project}/>
                                    )}
                                </Stack>
                            )}

                            {task && (
                                <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', rowGap: 0.5}}>
                                    {task.status && <Chip size="small" variant="outlined" label={task.status}/>}
                                    {task.activity && <Chip size="small" variant="outlined" label={task.activity}/>}
                                    {!!task.priority && (
                                        <Chip size="small" variant="outlined"
                                              label={t('calendar.priority', {value: task.priority})}/>
                                    )}
                                    {task.createdBy && (
                                        <Chip size="small" variant="outlined"
                                              label={`${t('calendar.createdBy')}: ${task.createdBy}`}/>
                                    )}
                                </Stack>
                            )}

                            <Divider/>

                            {/* Rohfelder — für den Abgleich mit der Plantafel. */}
                            <Typography variant="caption" color="text.secondary"
                                        sx={{fontFamily: 'monospace', wordBreak: 'break-word'}}>
                                {entry && <>
                                    id {entry.id} · headId {entry.headId} · montnr {entry.employeeId} ·
                                    belegart {entry.rawKind}
                                    {entry.orderId != null && <> · kdauftraglfdnr {entry.orderId}</>}
                                    {typeof entry.hours === 'number' && <> · {entry.hours} h</>}
                                    {entry.isPartOfSpan && <> · {t('calendar.partOfSpan')}</>}
                                    {entry.spanStart && <> · {dayjs(entry.spanStart).format('DD.MM. HH:mm')}</>}
                                    {entry.spanEnd && <>–{dayjs(entry.spanEnd).format('DD.MM. HH:mm')}</>}
                                </>}
                                {task && <>
                                    lfdnr {task.id} · perslfdnr {task.employeeId}
                                    {task.customerServiceId != null && <> · kdauftrag {task.customerServiceId}</>}
                                    {task.projectId != null && <> · projekt {task.projectId}</>}
                                    {' '}· inkalenderzeigen {String(task.showInCalendar)}
                                </>}
                            </Typography>
                        </Stack>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={onClose}>{t('calendar.close')}</Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default EntryDialog;
